/**
 * Reusable Solid ErrorBoundary with a PREMIUM, theme-aware fallback.
 *
 * Defense-in-depth layer ON TOP of TanStack Router's `errorComponent`:
 * captures render/effect errors in any subtree (including the editor,
 * stats, mood…) so a blank screen is never shown.
 *
 *  - `variant="screen"` (root): full-screen floating card.
 *  - `variant="region"` (sections): compact inline card that does not break
 *    the surrounding layout.
 *
 * "Reintentar" calls Solid's `reset()` (remounts the subtree, recovering
 * without reloading); "Recargar" performs a full reload as a last resort. The
 * error is always logged to the console.
 */
import { msg } from "@lingui/core/macro";
import { createEffect, ErrorBoundary, type JSX, Show } from "solid-js";
import { RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@journal/shared";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { BrokenJournalIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  variant: "screen" | "region";
  /** Region label for the log (e.g. "editor", "stats"). */
  label?: string;
  /** Custom title/description; defaults to a friendly generic message. */
  title?: string;
  description?: string;
}

function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  createEffect(() => {
    console.error(
      `[AppErrorBoundary${props.label ? `:${props.label}` : ""}] Caught error:`,
      props.error,
    );
  });

  const isScreen = () => props.variant === "screen";

  const detail = () =>
    props.error instanceof Error && props.error.message ? props.error.message : null;

  // Render helper (lowercase, invoked as a function — NOT a component) so each
  // branch of <Show> gets a fresh tree and the `isScreen()` reactive read stays
  // inside tracked JSX.
  const renderContent = () => (
    <div
      class={cn(
        "flex flex-col items-center text-center",
        isScreen() ? "space-y-6 p-8" : "gap-4 p-6",
      )}
      role="alert"
      data-testid="error-boundary-fallback"
    >
      <div class={cn("text-destructive", isScreen() && "animate-float")}>
        <BrokenJournalIllustration class={isScreen() ? "h-32 w-32" : "h-20 w-20"} />
      </div>
      <div class="space-y-2">
        <h2
          class={cn("font-bold tracking-tight", isScreen() ? "text-2xl" : "text-lg")}
          data-testid="error-boundary-title"
        >
          {props.title ?? t(msg`Something went wrong`)}
        </h2>
        <p class="mx-auto max-w-sm text-sm text-muted-foreground">
          {props.description ??
            t(msg`This part of your journal hit an unexpected error. Your entries are safe.`)}
        </p>
        <Show when={detail()}>
          {(d) => (
            <p class="mx-auto max-w-sm truncate text-[11px] text-muted-foreground/50">{d()}</p>
          )}
        </Show>
      </div>
      <div class={cn("flex gap-3", isScreen() && "w-full max-w-xs")}>
        <Button
          class="flex-1 gap-2"
          data-testid="error-boundary-retry"
          onClick={() => {
            props.reset();
          }}
        >
          <RotateCcw class="h-4 w-4" />
          {t(msg`Retry`)}
        </Button>
        <Button
          variant="outline"
          class="flex-1 gap-2"
          data-testid="error-boundary-reload"
          onClick={() => {
            globalThis.location.reload();
          }}
        >
          <RefreshCw class="h-4 w-4" />
          {t(msg`Reload`)}
        </Button>
      </div>
    </div>
  );

  return (
    <Show
      when={isScreen()}
      fallback={
        <div class="animate-fade-in rounded-2xl border border-destructive/30 bg-destructive/5">
          {renderContent()}
        </div>
      }
    >
      <div class="flex min-h-dvh w-full items-center justify-center bg-background p-4">
        <GlassSurface class="w-full max-w-md">{renderContent()}</GlassSurface>
      </div>
    </Show>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  variant?: "screen" | "region";
  /** Label for logging and diagnostics. */
  label?: string;
  title?: string;
  description?: string;
}

export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  // `fallback` is a render function (not a component): call ErrorFallback
  // directly so the linter doesn't flag a component defined during render.
  const renderFallback = (error: unknown, reset: () => void): JSX.Element =>
    ErrorFallback({
      error,
      reset,
      variant: props.variant ?? "region",
      label: props.label,
      title: props.title,
      description: props.description,
    });

  return <ErrorBoundary fallback={renderFallback}>{props.children}</ErrorBoundary>;
}
